// Archivo que maneja las propiedades de la barra lateral del editor de layouts
//

import React, { useMemo } from 'react'
import {
  Paper,
  TextField,
  MenuItem,
  Typography,
  Slider,
  Box,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
} from '@mui/material'
import { useDrag, useDrop } from 'react-dnd'
import { FIELD_STORAGE_OPTIONS, inferFieldStorage } from '../../utils/layoutUtils'
import {
  DEFAULT_WORKFLOW_WIDGET_CATALOG,
  readWorkflowWidgetCatalog,
} from '../../pages/settings/workflowWidgetCatalog'

const fieldTypes = [
  'Texto',
  'Número',
  'Fecha',
  'Hora',
  'Lista Desplegable',
  'workflow_stage_banner',
]

const fieldStorageOptions = [
  { value: FIELD_STORAGE_OPTIONS.CONSUMER, label: 'consumer' },
  { value: FIELD_STORAGE_OPTIONS.CUSTOM_FIELDS, label: 'custom_fields' },
  {
    value: FIELD_STORAGE_OPTIONS.WORKFLOW_RESOLVER,
    label: 'workflow_resolver',
  },
]

const fieldRoles = [
  { value: 'none', label: '— ninguno —' },
  { value: 'full_name', label: 'Nombre completo' },
  { value: 'phone', label: 'Teléfono' },
  { value: 'email', label: 'Correo' },
]

const WORKFLOW_MAPPING_ITEM_TYPE = 'WORKFLOW_WIDGET_MAPPING_SOURCE'

function DraggableMappingSource({ label, value, slot }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: WORKFLOW_MAPPING_ITEM_TYPE,
    item: { value, slot },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }))

  return (
    <Chip
      ref={drag}
      size="small"
      label={label}
      variant="outlined"
      sx={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
      }}
    />
  )
}

function MappingDropSlot({ label, value, acceptsSlot, onDropValue }) {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: WORKFLOW_MAPPING_ITEM_TYPE,
      canDrop: (item) => item?.slot === acceptsSlot,
      drop: (item) => onDropValue(item?.value),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [acceptsSlot, onDropValue]
  )

  return (
    <Box
      ref={drop}
      sx={{
        border: '1px dashed',
        borderColor: canDrop ? 'success.main' : 'divider',
        borderRadius: 1,
        p: 1,
        backgroundColor: isOver && canDrop ? 'success.light' : 'background.paper',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value || 'Suelta aquí'}</Typography>
    </Box>
  )
}

const normalizeNumber = (value, fallback, min = 0, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.max(min, Math.min(max, Math.round(parsed)))
}

const normalizeName = (raw) =>
  String(raw || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_|_$/g, '')

export default function PropertiesSidebar({ selectedField, onUpdate }) {
  const workflowCatalog = useMemo(readWorkflowWidgetCatalog, [])
  const workflowCatalogRoot =
    workflowCatalog?.workflow_stage_banner ||
    DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner

  const workflowResolverKeys =
    Array.isArray(workflowCatalogRoot.resolverKeys) &&
    workflowCatalogRoot.resolverKeys.length > 0
      ? workflowCatalogRoot.resolverKeys
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.resolverKeys

  const workflowModes =
    Array.isArray(workflowCatalogRoot.modes) &&
    workflowCatalogRoot.modes.length > 0
      ? workflowCatalogRoot.modes
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.modes

  const workflowPrefixOptions =
    Array.isArray(workflowCatalogRoot.prefixOptions) &&
    workflowCatalogRoot.prefixOptions.length > 0
      ? workflowCatalogRoot.prefixOptions
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.prefixOptions

  const workflowShapeOptions =
    Array.isArray(workflowCatalogRoot.shapeOptions) &&
    workflowCatalogRoot.shapeOptions.length > 0
      ? workflowCatalogRoot.shapeOptions
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.shapeOptions

  const workflowEmptyStateModes =
    Array.isArray(workflowCatalogRoot.emptyStateModes) &&
    workflowCatalogRoot.emptyStateModes.length > 0
      ? workflowCatalogRoot.emptyStateModes
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.emptyStateModes

  const workflowPresets =
    Array.isArray(workflowCatalogRoot.presets) &&
    workflowCatalogRoot.presets.length > 0
      ? workflowCatalogRoot.presets
      : DEFAULT_WORKFLOW_WIDGET_CATALOG.workflow_stage_banner.presets

  const defaultPresetId =
    typeof workflowCatalogRoot.defaultPreset === 'string'
      ? workflowCatalogRoot.defaultPreset
      : ''

  if (!selectedField) {
    return (
      <Typography variant="body2" color="text.secondary">
        Selecciona un campo en el canvas para editar sus propiedades.
      </Typography>
    )
  }

  const handleChange = (prop, value) => {
    onUpdate({ ...selectedField, [prop]: value })
  }

  const widgetConfig =
    selectedField?.config && typeof selectedField.config === 'object'
      ? selectedField.config
      : {}

  const updateWidgetConfig = (key, value) => {
    onUpdate({
      ...selectedField,
      config: {
        ...widgetConfig,
        [key]: value,
      },
    })
  }

  const isWorkflowStageBanner =
    String(selectedField?.type || '').trim().toLowerCase() === 'workflow_stage_banner'

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Typography variant="h6">Propiedades del campo</Typography>

      <Typography variant="subtitle2" color="text.secondary">
        Identidad
      </Typography>
      <TextField
        fullWidth
        label="Etiqueta visible (label)"
        value={selectedField.label || ''}
        onChange={(e) => handleChange('label', e.target.value)}
      />
      <TextField
        fullWidth
        label="Nombre técnico (name)"
        value={selectedField.name || ''}
        helperText="Se normaliza automáticamente para mantener compatibilidad"
        onChange={(e) => handleChange('name', normalizeName(e.target.value))}
      />
      <TextField
        select
        fullWidth
        label="Tipo de dato (type)"
        value={selectedField.type || 'Texto'}
        onChange={(e) => handleChange('type', e.target.value)}
      >
        {fieldTypes.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        fullWidth
        label="Rol semántico (role)"
        value={selectedField.role || 'none'}
        onChange={(e) => {
          const nextRole = e.target.value
          const shouldSyncStorage =
            !selectedField.storage ||
            inferFieldStorage(selectedField) === selectedField.storage

          onUpdate({
            ...selectedField,
            role: nextRole,
            ...(shouldSyncStorage
              ? {
                  storage: inferFieldStorage({
                    ...selectedField,
                    role: nextRole,
                  }),
                }
              : {}),
          })
        }}
      >
        {fieldRoles.map((r) => (
          <MenuItem key={r.value} value={r.value}>
            {r.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        fullWidth
        label="Storage"
        value={selectedField.storage || inferFieldStorage(selectedField)}
        onChange={(e) => handleChange('storage', e.target.value)}
      >
        {fieldStorageOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(selectedField.editable)}
            onChange={(e) => handleChange('editable', e.target.checked)}
          />
        }
        label="Editable"
      />

      {isWorkflowStageBanner ? (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="subtitle2" color="text.secondary">
            Configuración del widget workflow_stage_banner
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Mapeo visual guiado (drag & drop restringido)
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Fuentes disponibles
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {workflowResolverKeys.map((option) => (
                <DraggableMappingSource
                  key={`resolver-${option.value}`}
                  label={option.label}
                  value={option.value}
                  slot="resolverKey"
                />
              ))}
              {workflowModes.map((option) => (
                <DraggableMappingSource
                  key={`mode-${option.value}`}
                  label={option.label}
                  value={option.value}
                  slot="mode"
                />
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary">
              Slots del widget
            </Typography>
            <Stack spacing={1}>
              <MappingDropSlot
                label="Resolver de etapa"
                value={widgetConfig.resolverKey || 'workflow.current_stage'}
                acceptsSlot="resolverKey"
                onDropValue={(nextValue) => updateWidgetConfig('resolverKey', nextValue)}
              />
              <MappingDropSlot
                label="Modo de etapa"
                value={widgetConfig.mode || 'progress_stage'}
                acceptsSlot="mode"
                onDropValue={(nextValue) => updateWidgetConfig('mode', nextValue)}
              />
            </Stack>
          </Box>

          <TextField
            select
            fullWidth
            label="Preset"
            value={widgetConfig.presetId || defaultPresetId || ''}
            onChange={(e) => {
              const nextPresetId = e.target.value
              const selectedPreset = workflowPresets.find(
                (preset) => preset.value === nextPresetId
              )

              onUpdate({
                ...selectedField,
                config: {
                  ...widgetConfig,
                  presetId: nextPresetId,
                  ...(selectedPreset?.config &&
                  typeof selectedPreset.config === 'object'
                    ? selectedPreset.config
                    : {}),
                },
              })
            }}
          >
            <MenuItem value="">Sin preset</MenuItem>
            {workflowPresets.map((preset) => (
              <MenuItem key={preset.value} value={preset.value}>
                {preset.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Fuente de etapa"
            value={widgetConfig.resolverKey || 'workflow.current_stage'}
            onChange={(e) => updateWidgetConfig('resolverKey', e.target.value)}
          >
            {workflowResolverKeys.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Enfoque del seguimiento"
            value={widgetConfig.mode || 'progress_stage'}
            onChange={(e) => updateWidgetConfig('mode', e.target.value)}
          >
            {workflowModes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Texto inicial"
            value={widgetConfig.prefix || 'ETAPA'}
            onChange={(e) => updateWidgetConfig('prefix', e.target.value)}
          >
            {workflowPrefixOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Estilo visual"
            value={widgetConfig.shape || 'rounded'}
            onChange={(e) => updateWidgetConfig('shape', e.target.value)}
          >
            {workflowShapeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Nombre visible del banner (opcional)"
            value={widgetConfig.customLabel || widgetConfig.heading || widgetConfig.title || ''}
            onChange={(e) =>
              onUpdate({
                ...selectedField,
                config: {
                  ...widgetConfig,
                  customLabel: e.target.value,
                  heading: e.target.value,
                  title: e.target.value, // legacy compatibility
                },
              })
            }
          />

          <TextField
            fullWidth
            label="Mensaje cuando no hay etapa"
            value={widgetConfig.emptyMessage || ''}
            onChange={(e) => updateWidgetConfig('emptyMessage', e.target.value)}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={widgetConfig.showMetrics !== false}
                onChange={(e) => updateWidgetConfig('showMetrics', e.target.checked)}
              />
            }
            label="Mostrar métrica"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={widgetConfig.showStageNumber !== false}
                onChange={(e) => updateWidgetConfig('showStageNumber', e.target.checked)}
              />
            }
            label="Mostrar número de etapa"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(widgetConfig.showQuotes)}
                onChange={(e) => updateWidgetConfig('showQuotes', e.target.checked)}
              />
            }
            label="Mostrar comillas"
          />

          <TextField
            select
            fullWidth
            label="Comportamiento cuando no hay información"
            value={widgetConfig.emptyStateMode || widgetConfig.emptyBehavior || 'message'}
            onChange={(e) => {
              updateWidgetConfig('emptyStateMode', e.target.value)
              updateWidgetConfig('emptyBehavior', e.target.value) // legacy compatibility
            }}
          >
            {workflowEmptyStateModes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={
              <Checkbox
                checked={
                  widgetConfig.mode
                    ? widgetConfig.mode === 'billable_stage'
                    : widgetConfig.showBillableStage !== false
                }
                onChange={(e) => {
                  const nextMode = e.target.checked
                    ? 'billable_stage'
                    : 'progress_stage'

                  updateWidgetConfig('mode', nextMode)
                  updateWidgetConfig('showBillableStage', e.target.checked) // legacy compatibility
                }}
              />
            }
            label="Forzar modo facturable"
          />
        </>
      ) : null}

      <Divider sx={{ my: 0.5 }} />
      <Typography variant="subtitle2" color="text.secondary">
        Posición y tamaño
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="X"
          type="number"
          value={selectedField.x ?? 0}
          onChange={(e) =>
            handleChange(
              'x',
              normalizeNumber(e.target.value, selectedField.x ?? 0, 0, 2000)
            )
          }
          fullWidth
          inputProps={{ min: 0, max: 2000, step: 1 }}
        />
        <TextField
          label="Y"
          type="number"
          value={selectedField.y ?? 0}
          onChange={(e) =>
            handleChange(
              'y',
              normalizeNumber(e.target.value, selectedField.y ?? 0, 0, 2000)
            )
          }
          fullWidth
          inputProps={{ min: 0, max: 2000, step: 1 }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Ancho"
          type="number"
          value={selectedField.width ?? 180}
          onChange={(e) =>
            handleChange(
              'width',
              normalizeNumber(
                e.target.value,
                selectedField.width ?? 180,
                120,
                1200
              )
            )
          }
          fullWidth
          inputProps={{ min: 120, max: 1200, step: 1 }}
        />
        <TextField
          label="Alto"
          type="number"
          value={selectedField.height ?? 64}
          onChange={(e) =>
            handleChange(
              'height',
              normalizeNumber(
                e.target.value,
                selectedField.height ?? 64,
                52,
                800
              )
            )
          }
          fullWidth
          inputProps={{ min: 52, max: 800, step: 1 }}
        />
      </Box>

      <Divider sx={{ my: 0.5 }} />
      <Typography variant="subtitle2" color="text.secondary">
        Tipografía
      </Typography>
      <Box>
        <Typography variant="body2">
          Tamaño de fuente ({selectedField.fontSize || 14}px)
        </Typography>
        <Slider
          value={Number(selectedField.fontSize) || 14}
          onChange={(_, v) =>
            handleChange('fontSize', normalizeNumber(v, 14, 10, 36))
          }
          min={10}
          max={36}
          step={1}
          valueLabelDisplay="auto"
        />
      </Box>
    </Paper>
  )
}
